// SPDX-License-Identifier: Apache-2.0

import { ethers } from "ethers";
import detectEthereumProvider from "@metamask/detect-provider";
import i18n from "i18next";
import {
  getNetwork,
  getPublicKey,
  isAllowed,
  isConnected,
  requestAccess,
} from "@stellar/freighter-api";
import { CkbCCCProvider, FreighterProvider } from "../client/Provider";

interface MetaMaskError {
  code: number;
}

/**
 * @returns a promise for a Web3Provider initialized with the MetaMask provider
 * if it is available. Will reject if MetaMask is not detected.
 */
export async function initWeb3(): Promise<ethers.providers.Web3Provider> {
  const prov = await detectEthereumProvider({ mustBeMetaMask: true });
  if (prov) {
    return new ethers.providers.Web3Provider(
      prov as ethers.providers.ExternalProvider,
      "any",
    );
  } else {
    return Promise.reject("MetaMask not available");
  }
}

/**
 * Gets the Web3 provider that has to be connected to one of the given
 * networkIDs and requests an account.
 *
 * @param networkIDs - The IDs of the supported networks that MetaMask can be
 * connected to.
 * @returns - The Web3 Provider and the address of the provided account.
 * @throws - Error, if:
 * - Getting the Web3 Provider fails.
 * - MetaMask is not connected to one of the networkIDs.
 * - Requesting the accounts failed.
 */
function getMetaMaskProvider(): any {
  const eth = (window as any).ethereum;
  // If multiple providers are injected, choose MetaMask explicitly
  return eth?.providers?.find((p: any) => p.isMetaMask) ?? eth;
}

export async function onboardMM(networkIDs: bigint[]): Promise<{
  provider: ethers.providers.Web3Provider;
  account: string;
}> {
  const mm = getMetaMaskProvider();
  if (!mm?.isMetaMask) {
    console.log("no metamask");
    throw new Error(i18n.t("errorNoMetaMask"));
  }

  // IMPORTANT: call from a user gesture → this will open the popup if locked or not connected
  let accounts: string[];
  try {
    accounts = await mm.request({ method: "eth_requestAccounts" });
  } catch (e: any) {
    if (e?.code === 4001) throw new Error(i18n.t("rejectedAccountSelect")); // user canceled
    if (e?.code === -32002) throw new Error("MetaMask request pending. Open the extension to continue.");
    throw e;
  }
  if (!accounts?.length) throw new Error("No accounts returned by MetaMask");

  // (Optional) enforce a specific chain AFTER you have an account
  if (networkIDs?.length) {
    const toHex = (n: bigint) => "0x" + n.toString(16);
    const current = await mm.request({ method: "eth_chainId" });
    if (!networkIDs.map(toHex).includes(current)) {
      try {
        await mm.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: toHex(networkIDs[0]) }],
        });
      } catch (err: any) {
        if (err?.code === 4902) {
          // you could call wallet_addEthereumChain here if you have full chain params
          throw new Error("Please add/switch to the required network in MetaMask.");
        }
        throw err;
      }
    }
  }

  const provider = new ethers.providers.Web3Provider(mm, "any");
  return { provider, account: accounts[0] };
}

export async function onboardFr(network: string): Promise<{
  provider: FreighterProvider;
}> {
  const connected = await isConnected();
  if (!connected) {
    return Promise.reject("No Freighter installed");
  }
  const allowed = await isAllowed();
  if (allowed) {
    console.log("connected");
    let publicKey = "";
    let error = "";

    try {
      publicKey = await getPublicKey();
      if (publicKey === "") {
        return Promise.reject("Not logged in");
      }
      console.log(publicKey);
      const p = new FreighterProvider(network, publicKey);
      return { provider: p };
    } catch (e) {
      error = e as string;
    }

    if (error) {
      console.log(error);
      return Promise.reject(error);
    }
  } else {
    console.log("not connected");
    let publicKey = "";
    let error = "";

    try {
      publicKey = await requestAccess();
      console.log(publicKey);
      const p = new FreighterProvider(network, publicKey);
      return { provider: p };
    } catch (e) {
      error = e as string;
    }

    if (error) {
      console.log(error);
      return Promise.reject(error);
    }
  }
  return Promise.reject("No accounts specified");
}

export async function onboardCKB(network: string): Promise<{
  provider: CkbCCCProvider;
}> {
  return Promise.reject("No accounts specified");
}

/**
 * Requests the provider to switch the network to the one given by the chainID.
 *
 * @param provider - The Web3 Provider.
 * @param chainID - The ID of the network we want the provider to switch to.
 * @throws - An Error, if the request fails.
 */
export async function switchNetwork(
  provider: ethers.providers.Web3Provider,
  chainID: bigint,
): Promise<boolean> {
  let switched = false;
  console.log("switching network", chainID);
  try {
    await provider.provider.request?.({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: "0x" + chainID.toString(16) }],
    });
    switched = true;
    console.log("switched", chainID, switched);
    return switched;
  } catch (error: unknown) {
    if (!(error && (error as MetaMaskError).code)) {
      return switched;
    }
    const mmError = error as MetaMaskError;

    switch (mmError.code) {
      case 4902:
        throw new Error(i18n.t("errorNetworkNotAvailable"));
      case 4001:
        return switched;
      default:
        throw new Error(i18n.t("errorSwitchingNetwork"));
    }
  }
}

export async function getFreighterNetwork(): Promise<string> {
  if (await isConnected()) {
    let network = "";
    console.log("getting network");
    try {
      network = (await getNetwork()) as string;
      console.log(network);
    } catch (e) {
      console.log(e);
    }

    return network;
  }
  /*const accounts = await (window as any).rei.ckb.request({
    method: "ckb_requestAccounts",
  });
  console.log(accounts);*/

  return "Not connected";
}
